import { useState, useMemo, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
	ResponsiveContainer,
	LineChart,
	CartesianGrid,
	XAxis,
	YAxis,
	Tooltip,
	Legend,
	Line,
} from 'recharts';
import Button from '../../../components/atoms/button';
import Select from '../../../components/atoms/select';
import InputCalendar from '../../../components/molecules/input-calendar';
import Text from '../../../components/atoms/text';
import {
	generateFinancialPDF,
	preparePDFData,
} from '../../../utils/pdf-financial';
import { useRouteLoaderData } from 'react-router-dom';
import ReportsService from '../../../services/Reports.service';
import { NotificationToast } from '../../../components/organisms/notification-toast';
import { HomeLoaderData } from '../../../types/homeLoaderData';
import { generateFinancialXLSX } from '../../../utils/xlsx-financial';

export interface ChartData {
	date: string;
	monitorings: number;
	maintenances: number;
	totalMateriais: number;
}

const Financial = () => {
	const { user, accountId } = useRouteLoaderData('home') as HomeLoaderData;
	const { t } = useTranslation();

	const [dateInitial, setDateInitial] = useState<Date>(new Date());
	const [dateFinal, setDateFinal] = useState<Date>(new Date());
	const [chartData, setChartData] = useState<ChartData[] | null>(null);
	const [isLoading, setIsLoading] = useState(false);
	const [maintenanceValue, setMaintenanceValue] = useState(0);
	const [monitoringValue, setMonitoringValue] = useState(0);

	const handleGenerateReport = async () => {
		try {
			if (!accountId) return;
			setIsLoading(true);

			const response = await ReportsService.financialReports(
				Number(accountId),
				dateInitial,
				dateFinal
			);
			if (!response || !response?.chartData) {
				NotificationToast.Warn(
					'Não foram encontradas operações no período selecionado.'
				);
			} else {
				setChartData(response.chartData);
				setMaintenanceValue(response.maintenanceValue);
				setMonitoringValue(response.monitoringValue);
			}
		} catch (error) {
			console.log(error);
		} finally {
			setIsLoading(false);
		}
	};

	const handleGenerateXLSX = async () => {
		const pdfData = preparePDFData(
			chartData || [],
			monitoringValue,
			maintenanceValue
		);

		generateFinancialXLSX(pdfData, {
			dateInitial: dateInitial,
			dateFinal: dateFinal,
			clientName: '',
			totalIncome: totalIncome,
			monitoringUnitValue: monitoringValue,
			maintenanceUnitValue: maintenanceValue,
		});
	};

	const handleGeneratePDF = async () => {
		if (!chartData) return;

		const pdfData = preparePDFData(
			chartData,
			monitoringValue,
			maintenanceValue
		);

		// Obtenha o nome do cliente do Select (ajuste conforme sua implementação)
		const selectedClientName = ''; // Substitua por: clientSelectValue?.label || ''

		const pdfOptions = {
			dateInitial,
			dateFinal,
			clientName: '', // Nome do cliente ou valor padrão
			totalIncome,
			logoPath: '/logo.png',
			signaturePath: '/signature.png',
			monitoringUnitValue: monitoringValue,
			maintenanceUnitValue: maintenanceValue,
		};

		try {
			setIsLoading(true);
			const pdfDoc = await generateFinancialPDF(pdfData, pdfOptions);
			pdfDoc.save(
				`relatorio-financeiro-${selectedClientName || 'geral'}-${new Date().toISOString().split('T')[0]}.pdf`
			);
		} catch (error) {
			console.error('Erro ao gerar PDF:', error);
			alert('Erro ao gerar PDF. Tente novamente.');
		} finally {
			setIsLoading(false);
		}
	};
	// Cálculo do total baseado nos dados gerados
	const totalIncome = useMemo(() => {
		if (!chartData) return 0;

		return chartData.reduce((total, item) => {
			return (
				total +
				item.monitorings * monitoringValue +
				item.maintenances * maintenanceValue +
				item.totalMateriais
			);
		}, 0);
	}, [chartData, monitoringValue, maintenanceValue]);

	// Formatação de valores monetários
	const formatCurrency = (value: number): string => {
		return new Intl.NumberFormat('pt-BR', {
			style: 'currency',
			currency: 'BRL',
		}).format(value);
	};

	// Verifica se as datas são válidas para habilitar o botão
	const isGenerateDisabled = dateInitial > dateFinal;

	return (
		<div className="min-h-screen bg-gray-50">
			{/* Header e Filtros */}
			<div className="mb-8">
				{/* Card de Filtros */}
				<div className="bg-white rounded-lg shadow-sm p-6 mb-6">
					<div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
						<div>
							<label className="block text-sm font-medium text-gray-700 mb-2">
								{t('financial.startDate', 'Data Inicial')}
							</label>
							<InputCalendar
								date={dateInitial}
								onDateChange={setDateInitial}
								className="w-full"
							/>
						</div>

						<div>
							<label className="block text-sm font-medium text-gray-700 mb-2">
								{t('financial.endDate', 'Data Final')}
							</label>
							<InputCalendar
								date={dateFinal}
								onDateChange={setDateFinal}
								className="w-full"
							/>
						</div>

						<Button
							onClick={handleGenerateReport}
							disabled={isGenerateDisabled || isLoading}
							className={`w-full h-[42px] font-medium ${
								isGenerateDisabled || isLoading
									? 'bg-gray-400 cursor-not-allowed'
									: 'bg-blue-600 hover:bg-blue-700'
							} text-white`}
						>
							{isLoading ? (
								<div className="flex items-center justify-center">
									<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
									{t('financial.generating', 'Gerando...')}
								</div>
							) : (
								t('financial.generate', 'Gerar Relatório')
							)}
						</Button>
					</div>

					{/* Mensagem de validação de datas */}
					{isGenerateDisabled && (
						<div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-md">
							<Text as="p" className="text-red-700 text-sm">
								{t(
									'financial.dateValidation',
									'A data final deve ser maior ou igual à data inicial'
								)}
							</Text>
						</div>
					)}
				</div>

				{/* Card de Resumo - Só mostra quando há dados */}
				{chartData && (
					<div className="bg-white rounded-lg shadow-sm p-6 mb-6">
						<div className="flex flex-col sm:flex-row justify-between items-center">
							<div>
								<Text
									as="h2"
									className="text-gray-txt text-lg font-medium mb-1"
								>
									{t('financial.income', 'Receita Total')}
								</Text>
								<Text
									as="p"
									className="text-gray-label text-sm"
								>
									{t(
										'financial.periodSummary',
										'Período selecionado'
									)}
								</Text>
							</div>
							<div className="flex flex-col sm:flex-row items-center gap-4 mt-2 sm:mt-0">
								<Text
									as="h3"
									className="text-3xl font-bold text-primary-dark"
								>
									{formatCurrency(totalIncome)}
								</Text>
								<Button onClick={handleGenerateXLSX}>
									{t('financial.xlsx')}
								</Button>
								<Button
									onClick={handleGeneratePDF}
									disabled={isLoading}
									className="font-medium px-4 py-2 disabled:opacity-50"
								>
									<div className="flex items-center gap-2">
										{isLoading ? (
											<>
												<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
												{t(
													'financial.generatingPDF',
													'Gerando PDF...'
												)}
											</>
										) : (
											<>
												<svg
													className="w-4 h-4"
													fill="none"
													stroke="currentColor"
													viewBox="0 0 24 24"
												>
													<path
														strokeLinecap="round"
														strokeLinejoin="round"
														strokeWidth={2}
														d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
													/>
												</svg>
												{t(
													'financial.exportPDF',
													'Exportar PDF'
												)}
											</>
										)}
									</div>
								</Button>
							</div>
						</div>
					</div>
				)}
			</div>

			{/* Gráfico - Só mostra quando há dados */}
			{chartData ? (
				<div className="bg-white rounded-lg shadow-sm p-6 mb-6">
					<Text
						as="h3"
						className="text-lg font-semibold text-gray-900 mb-4"
					>
						{t(
							'financial.performanceChart',
							'Operações Realizadas'
						)}
					</Text>
					<ResponsiveContainer width="100%" height={400}>
						<LineChart data={chartData}>
							<CartesianGrid
								strokeDasharray="3 3"
								stroke="#f0f0f0"
							/>
							<XAxis
								dataKey="date"
								tick={{ fill: '#6b7280' }}
								tickLine={{ stroke: '#6b7280' }}
							/>
							<YAxis
								tick={{ fill: '#6b7280' }}
								tickLine={{ stroke: '#6b7280' }}
							/>
							<Tooltip
								contentStyle={{
									backgroundColor: 'white',
									border: 'none',
									borderRadius: '8px',
									boxShadow:
										'0 4px 6px -1px rgba(0, 0, 0, 0.1)',
								}}
								formatter={(value: number) => [value, '']}
							/>
							<Legend />
							<Line
								type="monotone"
								dataKey="monitorings"
								name={t(
									'financial.monitorings',
									'Monitoramentos'
								)}
								stroke="#8884d8"
								strokeWidth={2}
								dot={{ fill: '#8884d8', strokeWidth: 2, r: 4 }}
								activeDot={{ r: 6 }}
							/>
							<Line
								type="monotone"
								dataKey="maintenances"
								name={t(
									'financial.maintenances',
									'Manutenções'
								)}
								stroke="#82ca9d"
								strokeWidth={2}
								dot={{ fill: '#82ca9d', strokeWidth: 2, r: 4 }}
								activeDot={{ r: 6 }}
							/>
						</LineChart>
					</ResponsiveContainer>
				</div>
			) : (
				// Estado vazio - antes de gerar o primeiro relatório
				<div className="bg-white rounded-lg shadow-sm p-12 text-center mb-6">
					<div className="max-w-md mx-auto">
						<div className="text-gray-400 mb-4">
							<svg
								className="w-16 h-16 mx-auto"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
							>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth={1.5}
									d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
								/>
							</svg>
						</div>
						<Text
							as="h3"
							className="text-lg font-medium text-gray-900 mb-2"
						>
							{t(
								'financial.noDataTitle',
								'Nenhum relatório gerado'
							)}
						</Text>
						<Text as="p" className="text-gray-500 mb-6">
							{t(
								'financial.noDataDescription',
								"Configure os filtros e clique em 'Gerar Relatório' para visualizar os dados financeiros"
							)}
						</Text>
					</div>
				</div>
			)}

			{/* Valores Unitários - Sempre visíveis */}
			<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
				<div className="bg-white rounded-lg shadow-sm p-6">
					<div className="flex items-center justify-between">
						<div>
							<Text
								as="h4"
								className="text-sm font-medium text-gray-500 uppercase tracking-wide"
							>
								{t(
									'financial.maintenanceValue',
									'Valor da Manutenção'
								)}
							</Text>
							<Text as="p" className="text-xs text-gray-400 mt-1">
								{t('financial.perUnit', 'Por unidade')}
							</Text>
						</div>
						<Text
							as="p"
							className="text-2xl font-bold text-green-600"
						>
							{formatCurrency(maintenanceValue)}
						</Text>
					</div>
				</div>

				<div className="bg-white rounded-lg shadow-sm p-6">
					<div className="flex items-center justify-between">
						<div>
							<Text
								as="h4"
								className="text-sm font-medium text-gray-500 uppercase tracking-wide"
							>
								{t(
									'financial.monitoringValue',
									'Valor do Monitoramento'
								)}
							</Text>
							<Text as="p" className="text-xs text-gray-400 mt-1">
								{t('financial.perUnit', 'Por unidade')}
							</Text>
						</div>
						<Text
							as="p"
							className="text-2xl font-bold text-purple-600"
						>
							{formatCurrency(monitoringValue)}
						</Text>
					</div>
				</div>
			</div>
		</div>
	);
};

export default Financial;
